// src/stores/auth.js
import { defineStore } from "pinia";
import { ADMIN_LEVELS } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: localStorage.getItem("token") || null,
    refreshToken: localStorage.getItem("refresh_token") || null,
    profile: JSON.parse(localStorage.getItem("profile") || "null"),
    adminLevel:
      parseInt(localStorage.getItem("admin_level")) || ADMIN_LEVELS.DISABLED,
    adminLevelName: localStorage.getItem("admin_level_name") || "",
    passwordChangeRequired:
      localStorage.getItem("password_change_required") === "true",
    lastProfileRefresh: null,
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    profileType: (state) => state.profile?.profile_type || "",
    fullName: (state) =>
      state.profile
        ? `${state.profile.first_name || ""} ${
            state.profile.last_name || ""
          }`.trim()
        : "",
    // hasProfile: verifica se tem um perfil válido
    // Considera válido se tem token E profile não é null (simplificado para evitar edge cases)
    hasProfile: (state) =>
      !!(state.token && state.profile && Object.keys(state.profile).length > 0),

    // Novos getters para admin level
    isAdmin: (state) => state.adminLevel >= ADMIN_LEVELS.BASIC,
    adminLevelValue: (state) => state.adminLevel,
    adminLevelDisplayName: (state) => state.adminLevelName,

    // Getter para verificar se precisa trocar senha
    needsPasswordChange: (state) => state.passwordChangeRequired,
  },

  actions: {
    setAuth({
      token,
      refreshToken,
      profile,
      profile_role_level,
      profile_role_level_name,
      password_change_required,
    }) {
      this.token = token;
      this.refreshToken = refreshToken;
      this.profile = profile;
      this.adminLevel = profile_role_level || ADMIN_LEVELS.DISABLED;
      this.adminLevelName = profile_role_level_name || "";
      this.passwordChangeRequired = password_change_required || false;
      this.lastProfileRefresh = Date.now();

      logger.auth("setAuth chamado", {
        hasToken: !!token,
        hasProfile: !!profile,
        profileKeys: profile ? Object.keys(profile) : [],
        adminLevel: this.adminLevel,
        adminLevelName: this.adminLevelName,
        passwordChangeRequired: this.passwordChangeRequired,
      });

      localStorage.setItem("token", token);
      localStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("profile", JSON.stringify(profile));
      localStorage.setItem("admin_level", this.adminLevel);
      localStorage.setItem("admin_level_name", this.adminLevelName);

      if (this.passwordChangeRequired) {
        localStorage.setItem("password_change_required", "true");
      } else {
        localStorage.removeItem("password_change_required");
      }

      logger.auth("Dados salvos no localStorage", {
        admin_level: localStorage.getItem("admin_level"),
        admin_level_name: localStorage.getItem("admin_level_name"),
      });
    },

    /**
     * Atualiza o perfil do usuário a partir do backend
     * Usado para sincronizar admin level e outras informações
     */
    updateProfile({ profile, adminLevel, adminLevelName }) {
      const previousLevel = this.adminLevel;

      this.profile = profile;
      this.adminLevel = adminLevel;
      this.adminLevelName = adminLevelName;
      this.lastProfileRefresh = Date.now();

      localStorage.setItem("profile", JSON.stringify(profile));
      localStorage.setItem("admin_level", adminLevel);
      localStorage.setItem("admin_level_name", adminLevelName);

      // Log se houve mudança de nível
      if (previousLevel !== adminLevel) {
        logger.security("Admin level alterado", {
          previous: previousLevel,
          current: adminLevel,
          adminLevelName,
        });
      }
    },

    /**
     * Marca que a senha foi alterada com sucesso
     */
    clearPasswordChangeRequired() {
      this.passwordChangeRequired = false;
      localStorage.removeItem("password_change_required");
      logger.auth("Password change required cleared");
    },

    logout() {
      logger.auth("Iniciando logout completo");

      // Reseta o estado do store para os valores iniciais
      this.$reset();

      // Limpa todo storage do navegador
      localStorage.clear();
      sessionStorage.clear();

      // Limpa cookies relacionados (se houver)
      document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });

      logger.auth("Storage limpo, redirecionando para login");
      window.location.href = "/";
    },

    /**
     * Limpa o estado antes de um novo login
     * Deve ser chamado antes de setAuth para garantir estado limpo
     * NÃO usa $reset() porque isso re-lê do localStorage que pode estar inconsistente
     */
    clearBeforeLogin() {
      logger.auth("Limpando estado antes do login");

      // Limpa storage PRIMEIRO
      localStorage.clear();
      sessionStorage.clear();

      // Depois limpa o state diretamente (sem $reset que re-lê do localStorage)
      this.token = null;
      this.refreshToken = null;
      this.profile = null;
      this.adminLevel = ADMIN_LEVELS.DISABLED;
      this.adminLevelName = "";
      this.passwordChangeRequired = false;
      this.lastProfileRefresh = null;

      logger.auth("Estado limpo para novo login");
    },
  },
});
